import { z } from 'zod';

// src/addresses/model.ts
var ADDRESS_TYPES = [
  "wallet",
  "contract",
  "token",
  "nft",
  "exchange",
  "dex",
  "dex_pool",
  "mixer",
  "distributor",
  "proxy",
  "eoa",
  "unknown"
];
var ADDRESS_TYPE_LABEL = {
  wallet: "Wallet",
  contract: "Contract",
  token: "Token",
  nft: "NFT",
  exchange: "Exchange",
  dex: "DEX",
  dex_pool: "DEX pool",
  mixer: "Mixer",
  distributor: "Distributor",
  proxy: "Proxy",
  eoa: "EOA",
  unknown: "Unknown"
};
var CONTRACT_ADDRESS_TYPES = /* @__PURE__ */ new Set([
  "contract",
  "token",
  "nft",
  "proxy",
  "dex",
  "dex_pool",
  "mixer",
  "distributor"
]);
function isContractType(type) {
  return type != null && CONTRACT_ADDRESS_TYPES.has(type);
}
var ADDRESS_ENRICHMENT_STATUSES = ["processing", "ready", "failed"];
function isVerified(row) {
  return Boolean(row && (row.lifecycle === "verified" || row.is_human_approved === true));
}
function isBeingMapped(row) {
  return Boolean(row) && !isVerified(row) && row?.enrichment_status === "processing";
}
var ADDRESS_FIELD_DEFAULTS = {
  address_type: "unknown",
  risk_level: "safe",
  severity: "info",
  lifecycle: "candidate",
  is_primary_chain: false,
  is_human_approved: false,
  system_default: false,
  confidence: 1
};
function isDefaultedField(row, field) {
  const value = row?.[field];
  if (value === null || value === void 0) return true;
  const fallback = ADDRESS_FIELD_DEFAULTS[field];
  if (typeof value === "string" && typeof fallback === "string") {
    return value.trim().toLowerCase() === fallback;
  }
  return value === fallback;
}
function showsRecordedValue(row, field, annotated = false) {
  return annotated || !isDefaultedField(row, field);
}
function hasRecordedView(row) {
  return isVerified(row) || Boolean(row?.annotated);
}
var ADDRESS_LIST_MODES = ["tenant", "entity", "cross_chain"];
var ADDRESS_ROW_SCOPES = ["user", "tenant", "org", "customer", "global"];
var ADDRESS_ROW_SCOPE_LABELS = {
  user: "Your view",
  tenant: "Shared with tenant",
  org: "Organization",
  customer: "Customer",
  global: "Global"
};
var ADDRESS_ROW_SCOPE_DESCRIPTIONS = {
  user: "Your own view (your private overrides on top of the shared tiers).",
  tenant: "The view shared across your tenant (drops your personal overrides).",
  org: "An organization-level view, shared across this org's tenants.",
  customer: "A customer-level view, shared across the whole account.",
  global: "The shared global record of this address (no tenant overrides)."
};
function addressRowScopeLabel(scope) {
  return ADDRESS_ROW_SCOPE_LABELS[scope] ?? scope;
}
function addressRowScopeDescription(scope) {
  return ADDRESS_ROW_SCOPE_DESCRIPTIONS[scope] ?? "";
}
var ADDRESS_DEFAULT_PAGE_LIMIT = 50;
var ADDRESS_MAX_PAGE_LIMIT = 200;
var ADDRESS_SCOPES = {
  read: "addresses:show",
  share: "addresses:share",
  create: "addresses:create",
  edit: "addresses:edit",
  delete: "addresses:delete",
  manageDefaults: "addresses:manage-defaults",
  mapEntity: "addresses:map-entity",
  enrich: "addresses:enrich"
};
var MAX_BULK_MEMBERS = 100;

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key2, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key2);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key2) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key2);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/defi/position.ts
var PositionKind = {
  SUPPLY: "supply",
  BORROW: "borrow",
  STAKE: "stake",
  LIQUIDITY: "liquidity",
  FARM: "farm",
  VAULT: "vault",
  OTHER: "other"
};

// src/precedence/enums.generated.ts
var CatalogLifecycle = {
  VERIFIED: "verified",
  CANDIDATE: "candidate",
  DEPRECATED: "deprecated",
  QUARANTINED: "quarantined"
};
var Severity = {
  INFO: "info",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical"
};
var RiskLevel = {
  SAFE: "safe",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  SEVERE: "severe"
};

// src/precedence/risk.ts
var RISK_LEVEL_ORDER = [
  RiskLevel.SAFE,
  RiskLevel.LOW,
  RiskLevel.MEDIUM,
  RiskLevel.HIGH,
  RiskLevel.SEVERE
];
var RISK_LEVEL_RANK = Object.freeze(
  Object.fromEntries(RISK_LEVEL_ORDER.map((level, i) => [level, i]))
);
var RISK_VALUES = new Set(RISK_LEVEL_ORDER);
function coerceRisk(value) {
  const s = typeof value === "string" ? value.toLowerCase() : "";
  return RISK_VALUES.has(s) ? s : void 0;
}
function worstBy(items, map, levelOf) {
  let worst;
  let worstRank = -1;
  for (const item of items) {
    const mapped = map(item);
    if (mapped === void 0) continue;
    const rank = RISK_LEVEL_RANK[levelOf(mapped)] ?? 0;
    if (rank > worstRank) {
      worst = mapped;
      worstRank = rank;
    }
  }
  return worst;
}
function worstRisk(values) {
  return worstBy(values, coerceRisk, (level) => level);
}

// src/api/cache.ts
var MemoryRequestCache = class {
  static {
    brandClass(this, "api/MemoryRequestCache");
  }
  store = /* @__PURE__ */ new Map();
  maxEntries;
  defaultTtlMs;
  constructor(opts = {}) {
    this.maxEntries = opts.maxEntries ?? 200;
    this.defaultTtlMs = opts.defaultTtlMs ?? 3e4;
  }
  get(key2) {
    const entry = this.store.get(key2);
    if (!entry) return void 0;
    if (Date.now() > entry.expiresAt) {
      this.store.delete(key2);
      return void 0;
    }
    this.store.delete(key2);
    this.store.set(key2, entry);
    return entry.value;
  }
  set(key2, value, ttlMs = this.defaultTtlMs) {
    if (this.store.size >= this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest !== void 0) this.store.delete(oldest);
    }
    this.store.set(key2, { value, expiresAt: Date.now() + ttlMs });
  }
  delete(key2) {
    this.store.delete(key2);
  }
  clear() {
    this.store.clear();
  }
  keys() {
    return [...this.store.keys()];
  }
};
(class {
  static {
    brandClass(this, "api/LocalStorageRequestCache");
  }
  prefix;
  getStorage;
  constructor(opts = {}) {
    this.prefix = `${opts.namespace ?? "authe:api-cache"}:`;
    this.getStorage = opts.getStorage ?? (() => typeof localStorage !== "undefined" ? localStorage : void 0);
  }
  keyFor(key2) {
    return `${this.prefix}${key2}`;
  }
  get(key2) {
    const storage2 = this.getStorage();
    if (!storage2) return void 0;
    try {
      const raw = storage2.getItem(this.keyFor(key2));
      if (!raw) return void 0;
      const entry = JSON.parse(raw);
      if (Date.now() > entry.expiresAt) {
        storage2.removeItem(this.keyFor(key2));
        return void 0;
      }
      return entry.value;
    } catch {
      return void 0;
    }
  }
  set(key2, value, ttlMs) {
    const storage2 = this.getStorage();
    if (!storage2) return;
    try {
      const entry = { value, expiresAt: Date.now() + ttlMs };
      storage2.setItem(this.keyFor(key2), JSON.stringify(entry));
    } catch {
    }
  }
  delete(key2) {
    const storage2 = this.getStorage();
    if (!storage2) return;
    try {
      storage2.removeItem(this.keyFor(key2));
    } catch {
    }
  }
  clear() {
    const storage2 = this.getStorage();
    if (!storage2) return;
    try {
      const toRemove = [];
      for (let i = 0; i < storage2.length; i += 1) {
        const k = storage2.key(i);
        if (k && k.startsWith(this.prefix)) toRemove.push(k);
      }
      for (const k of toRemove) storage2.removeItem(k);
    } catch {
    }
  }
  keys() {
    const storage2 = this.getStorage();
    if (!storage2) return [];
    try {
      const out = [];
      for (let i = 0; i < storage2.length; i += 1) {
        const k = storage2.key(i);
        if (k && k.startsWith(this.prefix)) out.push(k.slice(this.prefix.length));
      }
      return out;
    } catch {
      return [];
    }
  }
});

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  OTHER: "other"
};

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  // Robinhood Chain (4663). Seeded alongside Ink because a Relay transfer
  // has a leg on each and, unseeded, the id resolved to nothing at all.
  "0x1237": "robinhood",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink",
  "robinhood"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x1237": { token: "ETH", gasUnit: "Gwei", name: "Robinhood Chain" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  robinhood: "https://robinhoodchain.blockscout.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized = chainIdHex.toLowerCase();
    if (!normalized.startsWith("0x")) normalized = "0x" + normalized.replace(/^0x/, "");
    return this.byHex.get(normalized) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toChainSlug(value) {
  if (!value) return null;
  return registry.resolve(value)?.slug ?? value;
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical(target) {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      target.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (target.get(slug)) continue;
    try {
      target.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
var registry = globalSingleton("chains/registry", () => {
  const seeded = new ChainRegistry();
  seedFromCanonical(seeded);
  return seeded;
});

// src/value/pricing.ts
var log = createLogger("fe-commons:pricing");
function priceRefKey(ref) {
  const chain = ref.chainId ? getChainMetadata(ref.chainId)?.slug ?? ref.chainId : "*";
  const id = (ref.address ?? ref.symbol ?? "?").toString().toLowerCase();
  return `${chain}:${id}`;
}
(class {
  constructor(prices) {
    this.prices = prices;
  }
  prices;
  static {
    brandClass(this, "value/StaticPriceSource");
  }
  name = "static";
  async fetchPrice(ref) {
    const price = this.prices[priceRefKey(ref)] ?? (ref.symbol ? this.prices[`*:${ref.symbol.toLowerCase()}`] : void 0);
    if (price == null) return null;
    return { priceUsd: price, asOf: Date.now(), source: this.name };
  }
});
(class {
  static {
    brandClass(this, "value/PriceEngine");
  }
  sources;
  cache;
  freshMs;
  pending = /* @__PURE__ */ new Map();
  /** Never-expiring last-known quotes, used when every source fails. */
  lastKnown = /* @__PURE__ */ new Map();
  constructor(opts) {
    this.sources = opts.sources;
    this.freshMs = opts.freshMs ?? 6e4;
    this.cache = opts.cache ?? new MemoryRequestCache({ defaultTtlMs: this.freshMs, maxEntries: 500 });
  }
  /** Resolve a single price (cached → sources in order → stale fallback → null). */
  getPrice(ref) {
    const key2 = priceRefKey(ref);
    const fresh = this.cache.get(key2);
    if (fresh) return Promise.resolve(fresh);
    const inFlight = this.pending.get(key2);
    if (inFlight) return inFlight;
    const p = this.resolveThroughSources(ref, key2).finally(() => this.pending.delete(key2));
    this.pending.set(key2, p);
    return p;
  }
  /** Batch resolve. Returns a map keyed by `priceRefKey`. */
  async getPrices(refs) {
    const out = /* @__PURE__ */ new Map();
    await Promise.all(
      refs.map(async (ref) => {
        out.set(priceRefKey(ref), await this.getPrice(ref));
      })
    );
    return out;
  }
  /** Drop a cached quote (e.g. on manual refresh). */
  invalidate(ref) {
    this.cache.delete(priceRefKey(ref));
  }
  async resolveThroughSources(ref, key2) {
    for (const source of this.sources) {
      try {
        const quote = await source.fetchPrice(ref);
        if (quote && Number.isFinite(quote.priceUsd)) {
          const resolved = { ...quote, source: quote.source || source.name };
          this.cache.set(key2, resolved, this.freshMs);
          this.lastKnown.set(key2, resolved);
          return resolved;
        }
      } catch (err) {
        log.warn(`source ${source.name} failed for ${key2}`, { err: String(err) });
      }
    }
    const stale = this.lastKnown.get(key2);
    if (stale) {
      log.debug(`serving stale price for ${key2} (all sources failed)`);
      return { ...stale, stale: true };
    }
    return null;
  }
});
function nativeTokenMeta(chainId) {
  const chain = getChainMetadata(chainId);
  return { symbol: chain?.nativeToken, decimals: chain?.decimals ?? 18 };
}
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var GROUPING_DIMENSIONS = [
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "kind",
  "saga"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});
function parseGroupingSpec(value) {
  if (!value || typeof value !== "object") return null;
  const raw = value;
  if (!Array.isArray(raw.dimensions)) return null;
  const known = new Set(GROUPING_DIMENSIONS);
  const seen = /* @__PURE__ */ new Set();
  const dimensions = [];
  for (const entry of raw.dimensions) {
    if (typeof entry !== "string" || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    dimensions.push(entry);
  }
  const options = {};
  if (raw.options && typeof raw.options === "object") {
    for (const [key2, opts] of Object.entries(raw.options)) {
      if (!known.has(key2)) continue;
      const parsed = GroupingDimensionOptionsSchema.safeParse(opts);
      if (parsed.success) options[key2] = parsed.data;
    }
  }
  return Object.keys(options).length > 0 ? { dimensions, options } : { dimensions };
}

// src/display/coerce.ts
function str(value) {
  return value == null || value === "" ? void 0 : String(value);
}
function num(value) {
  return coerceNumber(value);
}

// src/display/index.ts
var GRAPH_VIEW_MODES = ["hierarchical", "temporal", "force"];
GRAPH_VIEW_MODES.filter(
  (mode) => mode !== "force"
);
var GRAPH_DETAIL_TIERS = SCENE_DETAIL_TIERS;
var DEFAULT_GRAPH_DETAIL_TIER = "compact";
var GRAPH_PANEL_KEYS = ["legend", "highlights", "stats", "minimap"];
var ACTIVITY_VIEWS = ["table", "graph"];
var LIVE_MODES = ["live", "interval", "off"];
var TABLE_DENSITIES = ["comfortable", "normal", "compact"];
var DISPLAY_PREF_KEYS = {
  tableDensity: "display:table-density",
  columnVisibility: "display:columns",
  regionAliasPin: "display:region-alias-pin",
  graphViewMode: "display:graph-view-mode",
  graphDetailTier: "display:graph-detail-tier",
  graphHiddenPanels: "display:graph-panels",
  graphGrouping: "display:graph-grouping",
  activityView: "display:activity-view",
  liveMode: "display:live-mode",
  inspector: "display:inspector",
  attributionChips: "display:attribution-chips"
};
var ATTRIBUTION_KINDS = ["entity", "label", "tag", "group"];
var DEFAULT_REGION_ALIAS_PIN = "default";
function coerceEnum(value, allowed, fallback) {
  return typeof value === "string" && allowed.includes(value) ? value : fallback;
}
(class _DisplayPrefsStore {
  constructor(storage2) {
    this.storage = storage2;
  }
  storage;
  static {
    brandClass(this, "display/DisplayPrefsStore");
  }
  /** Derive a store for a different identity (e.g. on tenant switch). */
  with(patch) {
    return new _DisplayPrefsStore(this.storage.with(patch));
  }
  getTableDensity(fallback = "comfortable") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.tableDensity, fallback),
      TABLE_DENSITIES,
      fallback
    );
  }
  setTableDensity(density, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.tableDensity, density, level);
  }
  getGraphViewMode(fallback = "hierarchical") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphViewMode, fallback),
      GRAPH_VIEW_MODES,
      fallback
    );
  }
  setGraphViewMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphViewMode, mode, level);
  }
  getGraphDetailTier(fallback = DEFAULT_GRAPH_DETAIL_TIER) {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.graphDetailTier, fallback),
      GRAPH_DETAIL_TIERS,
      fallback
    );
  }
  setGraphDetailTier(tier, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.graphDetailTier, tier, level);
  }
  getActivityView(fallback = "table") {
    return coerceEnum(
      this.storage.get(DISPLAY_PREF_KEYS.activityView, fallback),
      ACTIVITY_VIEWS,
      fallback
    );
  }
  setActivityView(view, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.activityView, view, level);
  }
  getLiveMode(fallback = "interval") {
    return coerceEnum(this.storage.get(DISPLAY_PREF_KEYS.liveMode, fallback), LIVE_MODES, fallback);
  }
  setLiveMode(mode, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.liveMode, mode, level);
  }
  /** See {@link DisplayPrefs.inspector}. Anything non-boolean reads as the default. */
  getInspector(fallback = false) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.inspector, fallback);
    return typeof value === "boolean" ? value : fallback;
  }
  setInspector(enabled, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.inspector, enabled, level);
  }
  /**
   * See {@link DisplayPrefs.attributionChips}. Returns only the fields the user
   * has actually expressed, so the caller can layer it under a surface override
   * without a stored default silently outranking one.
   */
  getAttributionChips() {
    const raw = this.storage.get(DISPLAY_PREF_KEYS.attributionChips, {});
    if (!raw || typeof raw !== "object") return {};
    const stored = raw;
    const out = {};
    if (Array.isArray(stored.kinds)) {
      const kinds = stored.kinds.filter(
        (k) => ATTRIBUTION_KINDS.includes(k)
      );
      if (kinds.length > 0) out.kinds = kinds;
    }
    if (typeof stored.max === "number" && Number.isFinite(stored.max) && stored.max >= 0) {
      out.max = Math.floor(stored.max);
    }
    if (typeof stored.logos === "boolean") out.logos = stored.logos;
    if (stored.size === "sm" || stored.size === "md") out.size = stored.size;
    if (typeof stored.showProvenance === "boolean") out.showProvenance = stored.showProvenance;
    return out;
  }
  setAttributionChips(prefs, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.attributionChips, prefs, level);
  }
  getRegionAliasPin(fallback = DEFAULT_REGION_ALIAS_PIN) {
    const value = this.storage.get(DISPLAY_PREF_KEYS.regionAliasPin, fallback);
    return typeof value === "string" ? value : fallback;
  }
  setRegionAliasPin(alias, level) {
    return this.storage.set(DISPLAY_PREF_KEYS.regionAliasPin, alias, level);
  }
  /**
   * Overlay panels the user dismissed on one graph surface.
   *
   * Keyed per graph (as column visibility is keyed per table) because dismissing
   * the legend on a dense case graph shouldn't hide it on a two-node transaction
   * graph. Stores the DISMISSED set, not the visible one, so a panel added later
   * shows up by default instead of being retroactively hidden by an older pref.
   * Unknown keys are dropped on read.
   */
  getGraphHiddenPanels(graphId, fallback = []) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`,
      fallback
    );
    if (!Array.isArray(stored)) return fallback;
    return stored.filter(
      (key2) => GRAPH_PANEL_KEYS.includes(key2)
    );
  }
  setGraphHiddenPanels(graphId, hidden, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphHiddenPanels}:${graphId}`, [...hidden], level);
  }
  /**
   * How the user chose to band one graph surface (the "Group by" order).
   *
   * Keyed per graph for the same reason the panels are: grouping a case file by
   * entity says nothing about how a single transaction should be drawn. Parsed
   * on read, so a dimension this build no longer knows is dropped rather than
   * breaking the graph.
   */
  getGraphGrouping(graphId, fallback = null) {
    const stored = this.storage.get(
      `${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`,
      void 0
    );
    return parseGroupingSpec(stored) ?? fallback;
  }
  setGraphGrouping(graphId, grouping, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.graphGrouping}:${graphId}`, grouping, level);
  }
  /** Column visibility for one table id (`{ [columnId]: visible }`). */
  getColumnVisibility(tableId, fallback = {}) {
    return this.storage.get(
      `${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`,
      fallback
    );
  }
  setColumnVisibility(tableId, visibility, level) {
    return this.storage.set(`${DISPLAY_PREF_KEYS.columnVisibility}:${tableId}`, visibility, level);
  }
  /** Load the non-table-scoped prefs as one bag (column visibility is per-table). */
  load() {
    return {
      tableDensity: this.getTableDensity(),
      regionAliasPin: this.getRegionAliasPin(),
      graphViewMode: this.getGraphViewMode(),
      graphDetailTier: this.getGraphDetailTier(),
      activityView: this.getActivityView(),
      liveMode: this.getLiveMode(),
      inspector: this.getInspector(),
      attributionChips: this.getAttributionChips()
    };
  }
  /**
   * Persist whichever prefs are present (skips `undefined` fields).
   *
   * `true` only when every field it actually wrote persisted — same contract as
   * the individual setters, so a bulk save cannot claim more than they do.
   * Every field is attempted regardless: a full store is a property of the
   * store, not of one key, but stopping early would make the result depend on
   * field order.
   */
  save(prefs, level) {
    let stored = true;
    if (prefs.tableDensity) stored = this.setTableDensity(prefs.tableDensity, level) && stored;
    if (prefs.regionAliasPin != null) {
      stored = this.setRegionAliasPin(prefs.regionAliasPin, level) && stored;
    }
    if (prefs.graphViewMode) stored = this.setGraphViewMode(prefs.graphViewMode, level) && stored;
    if (prefs.graphDetailTier) {
      stored = this.setGraphDetailTier(prefs.graphDetailTier, level) && stored;
    }
    if (prefs.activityView) stored = this.setActivityView(prefs.activityView, level) && stored;
    if (prefs.liveMode) stored = this.setLiveMode(prefs.liveMode, level) && stored;
    if (prefs.inspector != null) stored = this.setInspector(prefs.inspector, level) && stored;
    if (prefs.attributionChips) {
      stored = this.setAttributionChips(prefs.attributionChips, level) && stored;
    }
    if (prefs.columnVisibility) {
      for (const [tableId, visibility] of Object.entries(prefs.columnVisibility)) {
        stored = this.setColumnVisibility(tableId, visibility, level) && stored;
      }
    }
    if (prefs.graphHiddenPanels) {
      for (const [graphId, hidden] of Object.entries(prefs.graphHiddenPanels)) {
        stored = this.setGraphHiddenPanels(graphId, hidden, level) && stored;
      }
    }
    if (prefs.graphGrouping) {
      for (const [graphId, grouping] of Object.entries(prefs.graphGrouping)) {
        stored = this.setGraphGrouping(graphId, grouping, level) && stored;
      }
    }
    return stored;
  }
});
var UNLIMITED_AMOUNTS = [
  (1n << 256n) - 1n,
  (1n << 255n) - 1n,
  (1n << 96n) - 1n
];
new Set(UNLIMITED_AMOUNTS.map((value) => value.toString()));
var DECIMAL_TEXT = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
function coerceNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value !== "string") return void 0;
  const text2 = value.trim();
  if (text2 === "" || !DECIMAL_TEXT.test(text2)) return void 0;
  const parsed = Number(text2);
  return Number.isFinite(parsed) ? parsed : void 0;
}

// src/addresses/wallet-data.ts
function describeWalletReadGap(gap, { subject, chainName }) {
  const notNone = "This is not a statement that none are held.";
  switch (gap.reason) {
    case "refused":
      return {
        title: `The provider won't list ${subject} for this address`,
        description: `The provider declines to list ${subject} for a wallet holding very many tokens on ${chainName}. ${notNone}`
      };
    case "unsupported_chain":
      return {
        title: `${capitalise(subject)} aren't available on ${chainName}`,
        description: `The wallet provider doesn't cover ${chainName}, so nothing is known here about what this address holds. ${notNone}`
      };
    case "rate_limited":
      return {
        title: `${capitalise(subject)} couldn't be read right now`,
        description: `The provider is limiting how often it can be asked. Try again shortly. ${notNone}`
      };
    default:
      return {
        title: `${capitalise(subject)} couldn't be read`,
        description: `The provider didn't answer for this address on ${chainName}. That is a gap in what could be read. ${notNone}`
      };
  }
}
function capitalise(text2) {
  return text2.charAt(0).toUpperCase() + text2.slice(1);
}
var enc = encodeURIComponent;
var walletDataPaths = {
  defi: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/defi`,
  balances: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/balances`,
  nfts: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/nfts`,
  nftCollection: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/nft-collection`,
  domain: (chain, address) => `/addresses/${enc(chain)}/${enc(address)}/domain`
};
function numOrUndef(v) {
  if (v == null) return void 0;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : void 0;
}
function defiKindFromLabel(label) {
  const l = (label ?? "").toLowerCase();
  if (/stak/.test(l)) return PositionKind.STAKE;
  if (/borrow|debt|loan/.test(l)) return PositionKind.BORROW;
  if (/suppl|lend|lent|deposit/.test(l)) return PositionKind.SUPPLY;
  if (/liquidity|pool|\blp\b/.test(l)) return PositionKind.LIQUIDITY;
  if (/farm|yield/.test(l)) return PositionKind.FARM;
  if (/vault/.test(l)) return PositionKind.VAULT;
  return PositionKind.OTHER;
}
function tokenToAsset(token, chainId) {
  return {
    symbol: token.symbol ?? token.name ?? "?",
    address: token.contract_address ?? void 0,
    chainId,
    amount: numOrUndef(token.balance_formatted),
    amountRaw: token.balance ?? void 0,
    decimals: token.decimals ?? void 0,
    priceUsd: token.usd_price ?? null,
    usdValue: token.usd_value ?? null,
    logoUrl: token.logo ?? token.thumbnail ?? null,
    ...legBucket(token.token_type) === "receipt" ? { receipt: true } : {}
  };
}
function legBucket(tokenType) {
  const t = (tokenType ?? "").trim().toLowerCase();
  if (!t) return "supplied";
  if (t === "defi-token" || t.includes("receipt")) return "receipt";
  if (t.includes("borrow") || t.includes("debt") || t.includes("loan")) return "borrowed";
  if (t.includes("reward") || t.includes("claimable") || t.includes("unclaimed")) return "rewards";
  return "supplied";
}
function legsByBucket(tokens, chainId) {
  const supplied = [];
  const borrowed = [];
  const rewards = [];
  for (const token of tokens) {
    const asset = tokenToAsset(token, chainId);
    const bucket = legBucket(token.token_type);
    if (bucket === "borrowed") borrowed.push(asset);
    else if (bucket === "rewards") rewards.push(asset);
    else supplied.push(asset);
  }
  return {
    ...supplied.length ? { supplied } : {},
    ...borrowed.length ? { borrowed } : {},
    ...rewards.length ? { rewards } : {}
  };
}
function walletDefiToPositions(defi, chainId) {
  const out = [];
  for (const proto of defi.protocols ?? []) {
    const protocolId = (proto.protocol_id ?? proto.protocol_name ?? "unknown").toLowerCase();
    (proto.positions ?? []).forEach((pos, i) => {
      out.push({
        id: `${pos.address ?? protocolId}-${i}`,
        protocolId,
        chainId,
        kind: defiKindFromLabel(pos.label),
        label: pos.label ?? proto.protocol_name ?? void 0,
        ...legsByBucket(pos.tokens ?? [], chainId),
        tvlUsd: pos.balance_usd ?? null,
        netUsd: pos.balance_usd ?? null,
        source: "moralis",
        metadata: {
          protocol_name: proto.protocol_name ?? void 0,
          protocol_logo: proto.protocol_logo ?? void 0,
          protocol_url: proto.protocol_url ?? void 0
        }
      });
    });
  }
  return out;
}
function walletDefiIsEmpty(defi) {
  if (!defi) return true;
  return !(defi.protocols ?? []).some((p) => (p.positions ?? []).length > 0);
}
function walletBalanceRows(balances, chainId) {
  const tokens = [...balances?.tokens ?? []];
  const native = balances?.native?.balance;
  if (native != null && native !== "" && !tokens.some((t) => t.native_token)) {
    const meta = nativeTokenMeta(chainId ?? balances?.chain);
    tokens.unshift({
      symbol: meta.symbol ?? null,
      decimals: meta.decimals ?? null,
      balance: String(native),
      usd_price: balances?.native?.usd_price ?? null,
      usd_value: balances?.native?.usd_value ?? null,
      native_token: true
    });
  }
  return tokens;
}
function partitionWalletBalances(balances, chainId) {
  const held = [];
  const spam = [];
  for (const token of walletBalanceRows(balances, chainId)) (token.possible_spam ? spam : held).push(token);
  const byValue = (a, b) => Number(Boolean(b.native_token)) - Number(Boolean(a.native_token)) || (b.usd_value ?? -1) - (a.usd_value ?? -1);
  return { held: held.sort(byValue), spam: spam.sort(byValue) };
}
function walletBalancesOutcome(balances, chainId) {
  if (!balances) return "unread";
  if (walletBalanceRows(balances, chainId).length > 0) return "held";
  if (balances.tokens == null) return "unread";
  return balances.native && balances.native.balance == null ? "unread" : "empty";
}
function walletBalancesGap(balances) {
  return balances?.tokens_unavailable ?? balances?.native?.unavailable ?? null;
}
function walletBalancesIsEmpty(balances, chainId) {
  return walletBalancesOutcome(balances, chainId) !== "held";
}
function partitionWalletNfts(page) {
  const held = [];
  const spam = [];
  for (const item of page?.items ?? []) (item.possible_spam ? spam : held).push(item);
  return { held, spam };
}
function walletNftPageIsEmpty(page) {
  return !page || (page.items ?? []).length === 0;
}
function mergeWalletNftPages(previous, next) {
  if (!previous) return next;
  const seen = new Set(
    (previous.items ?? []).map((i) => `${i.token_address ?? ""}:${i.token_id ?? ""}`)
  );
  const added = (next.items ?? []).filter(
    (i) => !seen.has(`${i.token_address ?? ""}:${i.token_id ?? ""}`)
  );
  return { ...next, items: [...previous.items ?? [], ...added] };
}
function walletNftItemToDto(item, chainId) {
  const meta = item.metadata_parsed ?? {};
  const collection = (item.token_address ?? "").toLowerCase();
  const metaName = typeof meta.name === "string" ? meta.name : null;
  const metaImage = typeof meta.image === "string" ? meta.image : null;
  const metaDesc = typeof meta.description === "string" ? meta.description : null;
  return {
    org_id: "main",
    tenant_id: "main",
    org_id_tenant_id: "main-main",
    customer_id: null,
    customer_org_tenant_pk_v2: "",
    chain_id: chainId,
    family: "evm",
    collection,
    token_id: item.token_id ?? "",
    owner: item.owner_of ?? null,
    name: item.name ?? metaName,
    description: metaDesc,
    image_uri: metaImage,
    metadata_uri: item.token_uri ?? null,
    collection_info: null,
    supply: item.amount ?? null,
    source: "moralis",
    metadata: {
      ...meta,
      rarity_rank: item.rarity_rank,
      rarity_percentage: item.rarity_percentage,
      rarity_label: item.rarity_label,
      floor_price_usd: item.floor_price_usd,
      possible_spam: item.possible_spam,
      verified_collection: item.verified_collection,
      list_price: item.list_price
    },
    chain_id_collection: `${chainId}#${collection}`
  };
}
function walletNftsToDtos(page, chainId) {
  return (page.items ?? []).map((item) => walletNftItemToDto(item, chainId));
}
function nftCollectionViewToCollection(view) {
  return {
    address: view.contract_address,
    name: view.name ?? null,
    symbol: view.symbol ?? null,
    isVerified: view.verified_collection ?? false,
    logoUrl: view.collection_logo ?? null,
    floorPriceUsd: view.floor_price_usd ?? null
  };
}

// src/pools/identity.ts
var POOL_MEMBER_ROLES = [
  "dex_pool",
  "pool",
  "liquidity_pool",
  "lp",
  "pair",
  "pool_pair"
];
var POOL_ROLE_SUFFIXES = ["pool", "pair", "lp"];
var AMM_POOL_QUALIFIERS = [
  "v1",
  "v2",
  "v3",
  "v4",
  "plain",
  "meta",
  "metapool",
  "crypto",
  "stable",
  "stableswap",
  "weighted",
  "concentrated",
  "constant",
  "amm"
];
var ROLE_SEPARATORS = /[-_.:/\s]+/;
var EXACT_ROLES = new Set(POOL_MEMBER_ROLES);
var SUFFIXES = new Set(POOL_ROLE_SUFFIXES);
var QUALIFIERS = new Set(AMM_POOL_QUALIFIERS);
function isPoolRole(role) {
  const value = String(role ?? "").trim().toLowerCase();
  if (!value) return false;
  if (EXACT_ROLES.has(value)) return true;
  const parts = value.split(ROLE_SEPARATORS).filter(Boolean);
  if (parts.length < 2) return false;
  const last = parts[parts.length - 1];
  const before = parts[parts.length - 2];
  if (last === void 0 || before === void 0) return false;
  return SUFFIXES.has(last) && QUALIFIERS.has(before);
}

// src/addresses/helpers.ts
var PROTECTED_FIELDS = /* @__PURE__ */ new Set([
  "chain",
  "address",
  "customer_id",
  "org_id",
  "tenant_id",
  "org_id_tenant_id",
  "customer_org_tenant_pk_v2",
  "id",
  "created_at",
  "created_by",
  "created_by_service",
  "updated_at",
  "updated_by",
  "updated_by_service"
]);
function addressRefPath(chain, address) {
  return `addresses/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
}
function clampAddressLimit(limit) {
  if (limit == null || Number.isNaN(limit)) return ADDRESS_DEFAULT_PAGE_LIMIT;
  return Math.max(1, Math.min(Math.trunc(limit), ADDRESS_MAX_PAGE_LIMIT));
}
function addressEditableBody(fields) {
  const out = {};
  for (const [k, v] of Object.entries(fields)) {
    if (PROTECTED_FIELDS.has(k)) continue;
    if (v === void 0) continue;
    out[k] = v;
  }
  return out;
}
function existingPrimaryChains(rows, address) {
  const target = (address ?? "").trim().toLowerCase();
  if (!target) return [];
  const seen = /* @__PURE__ */ new Set();
  for (const r of rows) {
    if (!r?.is_primary_chain) continue;
    if (String(r.address ?? "").trim().toLowerCase() !== target) continue;
    const chain = String(r.chain ?? "").trim();
    if (chain) seen.add(chain);
  }
  return [...seen];
}
function isContractLike(row) {
  if (!row) return false;
  if (row.is_contract != null) return row.is_contract === true;
  return isContractType(str(row.address_type));
}
var EOA_ADDRESS_TYPES = /* @__PURE__ */ new Set(["wallet", "eoa"]);
function isKnownNonContract(row) {
  if (!row) return false;
  if (row.is_contract === false) return true;
  if (row.is_contract === true) return false;
  return EOA_ADDRESS_TYPES.has(str(row.address_type) ?? "");
}
function addressColorOf(row) {
  const meta = row?.metadata;
  return str(meta?.color);
}
function addressTypeFromRoles(roles) {
  for (const raw of roles ?? []) {
    const role = str(raw);
    if (role && isPoolRole(role)) return { type: "dex_pool", role };
  }
  return void 0;
}
function addressLogoKeyOf(row) {
  return str(row?.logo_s3_uri);
}
function summarizeActivity(activity) {
  if (!activity) {
    return {
      activeChains: 0,
      totalTx: 0,
      totalDefi: 0,
      totalNft: 0,
      totalTokenTransfers: 0
    };
  }
  const chainCount = activity.active_chain_ids?.length || Object.keys(activity.chains ?? {}).length;
  return {
    activeChains: chainCount,
    totalTx: activity.total_tx_count ?? 0,
    totalDefi: activity.total_defi_count ?? 0,
    totalNft: activity.total_nft_count ?? 0,
    totalTokenTransfers: activity.total_token_transfer_count ?? 0,
    firstSeenAt: activity.first_seen_at ?? null,
    lastSeenAt: activity.last_seen_at ?? null
  };
}

// src/addresses/labels.ts
var SEVERITY_LABEL = {
  [Severity.INFO]: "Informational",
  [Severity.LOW]: "Low",
  [Severity.MEDIUM]: "Medium",
  [Severity.HIGH]: "High",
  [Severity.CRITICAL]: "Critical"
};
var LIFECYCLE_LABEL = {
  [CatalogLifecycle.VERIFIED]: "Verified",
  [CatalogLifecycle.CANDIDATE]: "Awaiting review",
  [CatalogLifecycle.DEPRECATED]: "Deprecated",
  [CatalogLifecycle.QUARANTINED]: "Quarantined"
};
function severityLabel(value) {
  return SEVERITY_LABEL[String(value ?? "")] ?? titleCase(value);
}
function lifecycleLabel(value) {
  return LIFECYCLE_LABEL[String(value ?? "")] ?? titleCase(value);
}
function approvalLabel(isHumanApproved) {
  return isHumanApproved === true ? "Confirmed by a person" : void 0;
}
var SOURCE_LABEL = {
  manual: "Entered by an operator",
  user: "Entered by an operator",
  reconciler: "Address reconciler",
  enrichment: "Enrichment pipeline",
  internal: "Internal data",
  provider: "Third-party provider",
  system: "Platform default"
};
function sourceLabel(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return void 0;
  return SOURCE_LABEL[raw.toLowerCase()] ?? raw;
}
function titleCase(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw.replace(/[_-]+/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// src/addresses/rollup.ts
function groupAddressesByAddress(rows, { fallbackChain = "eth" } = {}) {
  const groups = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const key2 = str(row.address);
    if (!key2) continue;
    const bucket = groups.get(key2);
    if (bucket) bucket.push(row);
    else groups.set(key2, [row]);
  }
  const out = [];
  for (const [address, groupRows] of groups) {
    const primaryChains = /* @__PURE__ */ new Set();
    const typeSet = /* @__PURE__ */ new Set();
    let name;
    let lastSeenAt;
    let color;
    let logoKey;
    for (const r of groupRows) {
      const chain = str(r.chain);
      if (chain && r.is_primary_chain) primaryChains.add(chain);
      const type = str(r.address_type);
      if (type && type !== "unknown") typeSet.add(type);
      if (!name) name = str(r.name);
      const rowColor = addressColorOf(r);
      if (rowColor && (color == null || r.is_primary_chain)) color = rowColor;
      const rowLogo = addressLogoKeyOf(r);
      if (rowLogo && (logoKey == null || r.is_primary_chain)) logoKey = rowLogo;
      const seen = num(r.last_seen_at);
      if (seen != null && (lastSeenAt == null || seen > lastSeenAt)) lastSeenAt = seen;
    }
    const chains = [
      ...new Set(groupRows.map((r) => str(r.chain)).filter((c) => Boolean(c)))
    ].sort((a, b) => Number(primaryChains.has(b)) - Number(primaryChains.has(a)));
    const riskLevel = worstRisk(groupRows.map((r) => r.risk_level));
    out.push({
      address,
      rows: groupRows,
      chains,
      primaryChains: [...primaryChains],
      ...name ? { name } : {},
      types: [...typeSet],
      ...riskLevel ? { riskLevel } : {},
      ...lastSeenAt != null ? { lastSeenAt } : {},
      ...color ? { color } : {},
      ...logoKey ? { logoKey } : {},
      preferredChain: chains.find((c) => primaryChains.has(c)) ?? chains[0] ?? fallbackChain
    });
  }
  out.sort((a, b) => (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0));
  return out;
}

// src/addresses/roles.ts
var MEMBERSHIP_ROLE_SUGGESTIONS = [
  {
    value: "treasury",
    label: "Treasury",
    hint: "Holds the company's own funds \u2014 the default for a corporate wallet."
  },
  {
    value: "hot_wallet",
    label: "Hot wallet",
    hint: "Operational, keys online: pays out, sweeps in, moves often."
  },
  {
    value: "cold_wallet",
    label: "Cold wallet",
    hint: "Long-term custody, keys offline: rarely moves, large balances."
  },
  {
    value: "deposit",
    label: "Deposit address",
    hint: "Receives from customers or counterparties; one of very many."
  },
  {
    value: "settlement",
    label: "Settlement",
    hint: "Nets off with a counterparty, exchange or market maker."
  },
  {
    value: "payroll",
    label: "Payroll",
    hint: "Pays people or contractors on a schedule."
  },
  {
    value: "contract_deployer",
    label: "Deployer",
    hint: "The EOA that deployed the entity's contracts."
  },
  {
    value: "contract",
    label: "Contract",
    hint: "A contract the entity operates (the address itself is code)."
  },
  {
    value: "bridge",
    label: "Bridge",
    hint: "Moves the entity's funds between chains."
  },
  {
    value: "fee",
    label: "Fee collector",
    hint: "Accrues protocol or trading fees."
  }
];
var MEMBERSHIP_ROLE_VALUES = MEMBERSHIP_ROLE_SUGGESTIONS.map((r) => r.value);
function membershipRoleSuggestion(role) {
  const needle = normalizeRole(role);
  if (!needle) return void 0;
  return MEMBERSHIP_ROLE_SUGGESTIONS.find(
    (r) => normalizeRole(r.value) === needle || normalizeRole(r.label) === needle
  );
}
function membershipRoleLabel(role) {
  if (!role) return "";
  return membershipRoleSuggestion(role)?.label ?? PLATFORM_ROLE_LABEL[normalizeRole(role)] ?? role;
}
var PLATFORM_ROLE_LABEL = {
  dexpool: "DEX pool",
  liquiditypool: "Liquidity pool"
};
function normalizeRole(role) {
  return (role ?? "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

// src/addresses/matrix.ts
var canonicalChain = (chain) => toChainSlug(chain) ?? chain.trim().toLowerCase();
var key = (chain, address) => `${canonicalChain(chain)}|${address.toLowerCase()}`;
function factChain(fact) {
  const row = fact;
  return String(row.chain ?? row.chain_id ?? "");
}
function membershipMatrix({
  memberships,
  chains,
  facts,
  primaryByChain = {}
}) {
  const looked = facts != null;
  const factByKey = /* @__PURE__ */ new Map();
  for (const fact of facts ?? []) {
    const row = fact;
    if (!row.address) continue;
    factByKey.set(key(factChain(fact), row.address), row);
  }
  const columns = [];
  const columnByChain = /* @__PURE__ */ new Map();
  const addColumn = (chain) => {
    if (!chain) return;
    const id = canonicalChain(chain);
    if (columnByChain.has(id)) return;
    columnByChain.set(id, chain);
    columns.push(chain);
  };
  if (chains) for (const chain of chains) addColumn(chain);
  const primaryByCanonicalChain = /* @__PURE__ */ new Map();
  for (const [chain, address] of Object.entries(primaryByChain)) {
    if (address) primaryByCanonicalChain.set(canonicalChain(chain), address.toLowerCase());
  }
  const rowByAddress = /* @__PURE__ */ new Map();
  for (const m of memberships) {
    const address = m.address;
    const chain = m.chain;
    if (!address || !chain) continue;
    if (!chains) addColumn(chain);
    const column = columnByChain.get(canonicalChain(chain));
    if (!column) continue;
    const lower = address.toLowerCase();
    let row = rowByAddress.get(lower);
    if (!row) {
      row = { address, cells: {} };
      rowByAddress.set(lower, row);
    }
    const fact = factByKey.get(key(chain, address));
    row.name = row.name ?? fact?.name ?? null;
    row.cells[column] = {
      state: primaryByCanonicalChain.get(canonicalChain(chain)) === lower ? "primary" : "member",
      known: looked ? fact != null : void 0,
      isContract: fact?.is_contract ?? null,
      addressType: fact?.address_type ?? null,
      role: m.role ?? null,
      tier: m.tier ?? null,
      active: m.active ?? void 0
    };
  }
  for (const [chain, address] of Object.entries(primaryByChain)) {
    if (!address) continue;
    const column = columnByChain.get(canonicalChain(chain));
    if (!column) continue;
    const lower = address.toLowerCase();
    let row = rowByAddress.get(lower);
    if (!row) {
      row = { address, cells: {} };
      rowByAddress.set(lower, row);
    }
    if (row.cells[column]) continue;
    const fact = factByKey.get(key(column, address));
    row.name = row.name ?? fact?.name ?? null;
    row.cells[column] = {
      state: "primary",
      known: looked ? fact != null : void 0,
      isContract: fact?.is_contract ?? null,
      addressType: fact?.address_type ?? null
    };
  }
  const rows = [];
  for (const row of rowByAddress.values()) {
    for (const chain of columns) {
      if (row.cells[chain]) continue;
      const fact = factByKey.get(key(chain, row.address));
      row.cells[chain] = {
        state: "none",
        known: looked ? fact != null : void 0,
        isContract: fact?.is_contract ?? null,
        addressType: fact?.address_type ?? null
      };
    }
    rows.push(row);
  }
  return { chains: columns, rows };
}
var MATRIX_ADDRESS_BUDGET = 100;
var MATRIX_FULL_LIMIT = 25;
var isDerived = (rows) => rows.length > 0 && rows.every((m) => m.tier === "derived");
var isCore = (rows) => rows.some((m) => m.tier === "core");
var isRetired = (rows) => rows.length > 0 && rows.every((m) => m.active === false);
function boundMatrixMemberships({
  memberships,
  primaryByChain = {},
  budget = MATRIX_ADDRESS_BUDGET,
  fullBelow = MATRIX_FULL_LIMIT
}) {
  const headlines = new Set(
    Object.values(primaryByChain).filter(Boolean).map((address) => address.toLowerCase())
  );
  const byAddress = /* @__PURE__ */ new Map();
  for (const m of memberships) {
    if (!m.address || !m.chain) continue;
    const lower = m.address.toLowerCase();
    const rows = byAddress.get(lower);
    if (rows) rows.push(m);
    else byAddress.set(lower, [m]);
  }
  let unloadedHeadlines = 0;
  for (const address of headlines) if (!byAddress.has(address)) unloadedHeadlines += 1;
  const total = byAddress.size + unloadedHeadlines;
  const classified = [...byAddress.values()].some(
    (rows) => rows.some((m) => m.tier === "core" || m.tier === "derived")
  );
  if (total <= fullBelow && !classified) {
    return {
      memberships: [...byAddress.values()].flat(),
      shown: total,
      total,
      curated: false,
      classified: false
    };
  }
  const spansChains = (rows) => {
    const seen = /* @__PURE__ */ new Set();
    for (const m of rows) if (m.chain) seen.add(canonicalChain(m.chain));
    return seen.size > 1;
  };
  const rank = (address, rows) => {
    if (headlines.has(address)) return 0;
    if (isDerived(rows)) return null;
    if (isCore(rows)) return 1;
    return spansChains(rows) ? 2 : 3;
  };
  const earned = [...byAddress.entries()].map(([address, rows], order) => ({
    rows,
    rank: rank(address, rows),
    retired: isRetired(rows),
    order
  })).filter(
    (entry) => entry.rank !== null
  );
  const kept = earned.sort(
    (a, b) => a.rank - b.rank || Number(a.retired) - Number(b.retired) || a.order - b.order
  ).slice(0, budget);
  return {
    memberships: kept.flatMap((entry) => entry.rows),
    shown: kept.length + unloadedHeadlines,
    total,
    // The budget is a cap on legibility; the filter is a statement about what the
    // grid is for. Only the latter changes what the caller should say.
    curated: earned.length < byAddress.size,
    classified
  };
}

// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  PERM002: spec("PERM002", "Resource access denied", "You do not have access to this resource within your tenant.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // A plan's usage quota is used up for its window. 429, but NOT retryable: an
  // unknown 429 is retried by the client, and re-sending a refused run cannot
  // clear a monthly cap. `details.resets_at` says when it does.
  QUOTA001: spec("QUOTA001", "Usage quota reached", "Wait until the quota resets, or move to a plan with a higher quota. The message says which quota and when it resets.", ErrorCategory.RATE_LIMIT, 429, { severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}
(class _AutheError extends Error {
  static {
    brandClass(this, "errors/AutheError");
  }
  spec;
  details;
  correlationId;
  constructor(spec2, opts = {}) {
    super(opts.message ?? spec2.description);
    this.name = "AutheError";
    this.spec = spec2;
    this.details = opts.details ?? {};
    this.correlationId = opts.correlationId;
    if (opts.cause !== void 0) this.cause = opts.cause;
  }
  /** Look up a code by string id and build an `AutheError` (falls back to INT001). */
  static fromCode(code, opts = {}) {
    return new _AutheError(getCodeSpec(code) ?? ErrorCode.INT001, opts);
  }
  get retryable() {
    return this.spec.retryable;
  }
  toBody() {
    return {
      message: this.message,
      error_type: this.name,
      error_code: this.spec.code,
      error_level: this.spec.severity,
      details: this.details,
      correlation_id: this.correlationId ?? null
    };
  }
  toResponse() {
    return { statusCode: this.spec.defaultStatus, ok: false, error: this.toBody() };
  }
});

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  static {
    brandClass(this, "api/ApiError");
  }
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}
var ScopeOptionSchema = z.object({
  scope: z.string(),
  readable: z.boolean().default(true),
  count: z.number().int().default(1),
  overrides: z.record(z.string(), z.string()).default({})
});
var CrossChainMemberSchema = z.object({
  tx_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  role: z.union([z.string(), z.null()]).default(null),
  roles: z.array(z.string()).default([]),
  is_self: z.boolean().default(false),
  // Per-leg execution outcome, so the card can flag a reverted counterpart
  // leg (a bridge destination that landed but reverted) with its reason.
  // `status` is the semantic status (`success`/`failed`/…), `receipt_status`
  // the raw EVM word (1/0), `error` the revert reason; `failed` is derived.
  status: z.union([z.string(), z.null()]).default(null),
  receipt_status: z.union([z.number(), z.null()]).default(null),
  error: z.union([z.string(), z.null()]).default(null),
  failed: z.boolean().default(false)
}).catchall(z.any());
var CrossChainGroupSchema = z.object({
  group_id: z.string(),
  saga_kind: z.union([z.string(), z.null()]).default(null),
  relationship_type: z.union([z.string(), z.null()]).default(null),
  linkage_model: z.union([z.string(), z.null()]).default(null),
  group_scope: z.union([z.string(), z.null()]).default(null),
  role: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number(), z.null()]).default(null),
  confidence_tier: z.union([z.string(), z.null()]).default(null),
  state: z.union([z.string(), z.null()]).default(null),
  // Legs the group holds — the store's own count, keyed on `(tx_id, role)`.
  member_count: z.union([z.number(), z.null()]).default(null),
  // Distinct transactions behind those legs, i.e. `members.length`. Lower than
  // `member_count` whenever a transaction plays more than one part, which is
  // why a card counting transactions must read this one: 18 transactions can
  // produce 22 legs of a pool's lifecycle.
  transaction_count: z.union([z.number(), z.null()]).default(null),
  members: z.array(CrossChainMemberSchema).default([]),
  // Live grouping lifecycle: canonical roles still expected (a lone source's
  // `["destination"]`), whether the group is waiting on a counterpart, and the
  // unix-seconds timeout — so the detail card can show "awaiting destination"
  // with a countdown, symmetric with the group page.
  expected_roles: z.array(z.string()).default([]),
  missing_roles: z.array(z.string()).default([]),
  awaiting: z.boolean().default(false),
  // True for a group that accretes legs and has no fixed counterpart — a
  // wallet's `trade.token_position`, a `pool.lifecycle`, a `token.launch`. Such
  // a group is never "awaiting a leg". A lifecycle fact only: it does not say
  // WHICH of the three this is, so route on `saga_kind`.
  open_ended: z.boolean().default(false),
  // The group's subject: the protocol's own name for the identifier every leg
  // shares (`pool`, `message_id` — feed it to `sagaIdLabel`) and its value (the
  // pool address, the `messageId`). A card cannot name what a group is ABOUT
  // without this: "Pool activity" states an activity but not a pool.
  id_label: z.union([z.string(), z.null()]).default(null),
  id_value: z.union([z.string(), z.null()]).default(null),
  // For a wallet's position only: the token address the position is in + the
  // venue it traded on (e.g. `uniswap v2`), lifted off the legs so the card
  // can title the position by token. `null` for every other shape — a pool's
  // lifecycle has no single trader and no one token.
  token: z.union([z.string(), z.null()]).default(null),
  venue: z.union([z.string(), z.null()]).default(null),
  expires_at: z.union([z.number(), z.null()]).default(null),
  // True when any leg's execution reverted (a broken saga) — derived from the
  // members' per-leg status so the card can surface it even while state=open.
  failed: z.boolean().default(false)
}).catchall(z.any());
var ChainForkSchema = z.object({
  chain_id: z.string(),
  canonical_tx_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null)
}).catchall(z.any());
z.object({
  canonical_tx_id: z.string(),
  chain_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null),
  // Tenancy of the SERVED row — lets the FE show whose/which copy this is.
  customer_id: z.union([z.string(), z.null()]).default(null),
  org_id: z.union([z.string(), z.null()]).default(null),
  tenant_id: z.union([z.string(), z.null()]).default(null),
  user_id: z.union([z.string(), z.null()]).default(null),
  // Body pointer (the heavy payload stays in S3 / on the CDN).
  has_body: z.boolean().default(false),
  body_key: z.union([z.string(), z.null()]).default(null),
  body_etag: z.union([z.string(), z.null()]).default(null),
  body: z.union([z.record(z.string(), z.any()), z.null()]).default(null),
  // Manifest of heavy body parts offloaded to sibling S3 objects (`raw` /
  // `events` / `decoded`), each with its own byte size + etag. Empty when the
  // whole body is a single "core" object. Lets the detail UI paint the core
  // immediately and lazy-load + merge the heavy slices on demand (mirrors
  // `TransactionRow.body_slices` / `EnvelopeHeader.body_slices`).
  body_slices: z.record(
    z.string(),
    z.object({
      bytes: z.number().int().default(0),
      etag: z.union([z.string(), z.null()]).default(null)
    }).catchall(z.any())
  ).default({}),
  // Async background-enrichment state. `processing` = the decode/graph is
  // running on the pipeline (the row is a fast stub with no body yet), `ready`
  // = the body/graph have landed (== `has_body`), `failed` = the run errored,
  // `null` = the tx was never part of the async flow (already enriched / sync
  // live-fill). `run_id` is the enqueued pipeline run, for polling/diagnostics.
  // The FE polls the detail while `processing`, then loads the body on `ready`.
  enrichment_status: z.union([z.string(), z.null()]).default(null),
  run_id: z.union([z.string(), z.null()]).default(null),
  // Workspace + cross-scope affordance.
  saved: z.boolean().default(false),
  other_scopes: z.array(ScopeOptionSchema).default([]),
  // Cross-chain saga groups this tx is a leg of (source↔destination linkage),
  // resolved at read time from the protocols' `cross_chain_refs`.
  cross_chain_groups: z.array(CrossChainGroupSchema).default([]),
  // Forked chains (ETH/ETC) the SAME raw hash is also on — the same hash is a
  // distinct tx per chain, so each is a separate row the FE can switch to.
  chain_forks: z.array(ChainForkSchema).default([])
}).catchall(z.any());
function scopeReadQuery(scope, opts = {}) {
  const query = {};
  if (opts.includeBody) query.include_body = true;
  if (opts.enqueue === false) query.enqueue = false;
  if (scope) {
    for (const [key2, value] of Object.entries(scope.overrides ?? {})) {
      query[key2] = value;
    }
  }
  return query;
}

// src/addresses/api.ts
var MissingRouteError = class extends Error {
  constructor(path, envelope) {
    super(`route ${path} is not available on this backend (HTTP ${envelope.statusCode})`);
    this.path = path;
    this.name = "MissingRouteError";
    this.statusCode = envelope.statusCode;
  }
  path;
  static {
    brandClass(this, "addresses/MissingRouteError");
  }
  statusCode;
};
function isMissingRoute(res) {
  if (res.ok) return false;
  if (res.statusCode !== 403 && res.statusCode !== 404) return false;
  if (res.error?.error_code) return false;
  const message = (res.error?.message ?? "").toLowerCase();
  return message.includes("missing authentication token") || message.includes("not found") || message === "forbidden";
}
function createAddressesApi(client, options = {}) {
  const base = (options.basePath ?? "/addresses").replace(/\/+$/, "");
  const ref = (chain, address) => `${base}/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`;
  const memberships = `${base}/entity-memberships`;
  return {
    async list(query, signal) {
      const q = {
        // The wire calls this `scope`; the domain calls it `mode`. THE one
        // translation — see `AddressListMode` for why the domain refuses the
        // word. It is not a tenancy rung and this route takes none.
        scope: query?.mode ?? "tenant",
        limit: clampAddressLimit(query?.limit)
      };
      if (query?.entity_id) q.entity_id = query.entity_id;
      const address = query?.address?.trim();
      if (address) q.address = address;
      if (query?.include_global) q.include_global = true;
      if (query?.cursor) q.cursor = query.cursor;
      if (query?.address_type) q.address_type = query.address_type;
      if (query?.chain_ids?.length) q.chain_ids = query.chain_ids.join(",");
      const page = unwrapData(await client.get(base, { query: q, signal, cacheTtlMs: 0 }));
      const addresses = Array.isArray(page?.addresses) ? page.addresses : [];
      return { addresses, next_cursor: page?.next_cursor ?? null, count: page?.count ?? addresses.length };
    },
    async get(chain, address, signal) {
      return unwrapData(await client.get(ref(chain, address), { signal, cacheTtlMs: 0 })).address;
    },
    async getDetail(chain, address, opts, signal) {
      const query = { ...scopeReadQuery(opts?.scope ?? null) };
      if (opts?.withScopes) query.scopes = true;
      const res = unwrapData(
        await client.get(ref(chain, address), { query, signal, cacheTtlMs: 0 })
      );
      return {
        address: res?.address,
        saved: res?.saved ?? false,
        enrichment_status: res?.enrichment_status ?? null,
        catalog: res?.catalog,
        other_scopes: Array.isArray(res?.other_scopes) ? res.other_scopes : [],
        own_override: res?.own_override ?? false
      };
    },
    async getByAddress(address, signal) {
      const res = unwrapData(
        await client.get(base, {
          query: { scope: "cross_chain", address },
          signal,
          cacheTtlMs: 0
        })
      );
      const addresses = (Array.isArray(res?.addresses) ? res.addresses : []).map((row) => ({
        ...row,
        saved: row?.saved ?? true,
        enrichment_status: row?.enrichment_status ?? null,
        // Defaults FALSE, unlike `saved`: claiming an annotation nobody made
        // would show a row's defaults as somebody's judgement.
        annotated: row?.annotated ?? false
      }));
      return { addresses, activity: res?.activity ?? null, count: res?.count ?? addresses.length };
    },
    async create(input) {
      const { chain, address, ...rest } = input;
      const body = { chain, address, ...addressEditableBody(rest) };
      return unwrapData(await client.post(base, body)).address;
    },
    async createMany(input) {
      const body = {
        ...addressEditableBody(input.fields ?? {}),
        address: input.address
      };
      if (input.chains?.length) body.chains = input.chains;
      if (input.all_chains) body.all_chains = true;
      if (input.per_chain && Object.keys(input.per_chain).length > 0) {
        body.per_chain = Object.fromEntries(
          Object.entries(input.per_chain).map(([chain, fields]) => [
            chain,
            addressEditableBody(fields ?? {})
          ])
        );
      }
      if (input.assignments?.length) {
        body.assignments = input.assignments.map((a) => ({
          catalog_kind: a.catalog_kind,
          catalog_id: a.catalog_id,
          user_scoped: a.user_scoped ?? true,
          ...a.visible_to_user_ids ? { visible_to_user_ids: a.visible_to_user_ids } : {},
          ...a.visible_to_team_ids ? { visible_to_team_ids: a.visible_to_team_ids } : {}
        }));
      }
      if (input.set_primary_entity_id) body.set_primary_entity_id = input.set_primary_entity_id;
      const res = unwrapData(await client.post(base, body));
      return {
        results: Array.isArray(res?.results) ? res.results : [],
        addresses: Array.isArray(res?.addresses) ? res.addresses : [],
        count: res?.count ?? 0,
        failed: res?.failed ?? 0,
        overlays: Array.isArray(res?.overlays) ? res.overlays : [],
        primary: Array.isArray(res?.primary) ? res.primary : []
      };
    },
    async update(chain, address, changes) {
      return unwrapData(
        await client.patch(ref(chain, address), addressEditableBody(changes))
      ).address;
    },
    async remove(chain, address, scope = "user") {
      unwrapData(await client.delete(ref(chain, address), { query: { scope } }));
    },
    async getActivity(chain, address, signal) {
      const res = unwrapData(
        await client.get(`${ref(chain, address)}/activity`, {
          signal,
          cacheTtlMs: 0
        })
      );
      return res.activity ?? null;
    },
    async listAssignments(chain, address, signal) {
      const res = unwrapData(
        await client.get(`${ref(chain, address)}/assignments`, {
          signal,
          cacheTtlMs: 0
        })
      );
      return res.assignments ?? [];
    },
    async attachAssignment(chain, address, input) {
      const { user_scoped, ...rest } = input;
      return unwrapData(
        await client.post(`${ref(chain, address)}/assignments`, {
          ...rest,
          user_scoped: user_scoped ?? true
        })
      ).assignment;
    },
    async detachAssignment(chain, address, input) {
      unwrapData(
        await client.delete(`${ref(chain, address)}/assignments`, {
          query: {
            catalog_kind: input.catalog_kind,
            catalog_id: input.catalog_id,
            // Sent only when the caller MEANS one lane. Absent, the route clears
            // whichever partition holds the overlay — the private one or the
            // tenant-wide one. This used to default to `true`, which made every
            // detach of a tenant-wide overlay delete a key that did not exist:
            // a 200, an unchanged chip, and no way to remove it at all.
            ...input.user_scoped === void 0 ? {} : { user_scoped: input.user_scoped }
          }
        })
      );
    },
    async share(chain, address, input) {
      return unwrapData(
        await client.post(`${ref(chain, address)}/share`, {
          catalog_kind: input.catalog_kind,
          catalog_id: input.catalog_id,
          user_scoped: input.user_scoped ?? true,
          add_user_ids: input.add_user_ids,
          add_team_ids: input.add_team_ids,
          remove_user_ids: input.remove_user_ids,
          remove_team_ids: input.remove_team_ids
        })
      ).assignment;
    },
    async setPrimary(chain, address, entityId) {
      return unwrapData(
        await client.put(`${ref(chain, address)}/primary`, {
          entity_id: entityId
        })
      ).primary;
    },
    async clearPrimary(chain, address, entityId) {
      unwrapData(
        await client.delete(`${ref(chain, address)}/primary`, { query: { entity_id: entityId } })
      );
    },
    async getEntityMapping(chain, address, signal) {
      const res = await client.get(`${ref(chain, address)}/entity`, {
        signal,
        cacheTtlMs: 0
      });
      if (res.ok) {
        return res.data?.mapping ?? res.result?.mapping ?? null;
      }
      if (isMissingRoute(res)) throw new MissingRouteError(`${ref(chain, address)}/entity`, res);
      if (res.statusCode === 404) return null;
      unwrapData(res);
      return null;
    },
    async putEntityMapping(chain, address, input) {
      return unwrapData(
        await client.put(`${ref(chain, address)}/entity`, {
          entity_id: input.entity_id,
          ...input.role !== void 0 ? { role: input.role } : {},
          ...input.tier !== void 0 ? { tier: input.tier } : {},
          ...input.confidence !== void 0 ? { confidence: input.confidence } : {},
          ...input.valid_from !== void 0 ? { valid_from: input.valid_from } : {},
          ...input.valid_to !== void 0 ? { valid_to: input.valid_to } : {},
          ...input.source !== void 0 ? { source: input.source } : {},
          ...input.metadata !== void 0 ? { metadata: input.metadata } : {}
        })
      ).mapping;
    },
    async deleteEntityMapping(chain, address) {
      unwrapData(await client.delete(`${ref(chain, address)}/entity`));
    },
    async listEntityMemberships(query, signal) {
      const res = await client.get(memberships, {
        query: {
          entity_id: query.entity_id,
          limit: clampAddressLimit(query.limit),
          ...query.cursor ? { cursor: query.cursor } : {},
          ...query.chains?.length ? { chains: query.chains.join(",") } : {},
          ...query.prefer_core ? { core: "true" } : {},
          ...query.address_type ? { address_type: query.address_type } : {},
          // `chains` above widens the headline pointer's coverage; this narrows
          // the rows. Two parameters that both name chains, so they are spelled
          // apart on the wire as well as in the type.
          ...query.chain_ids?.length ? { chain_ids: query.chain_ids.join(",") } : {}
        },
        signal,
        cacheTtlMs: 0
      });
      if (!res.ok && isMissingRoute(res)) throw new MissingRouteError(memberships, res);
      const page = unwrapData(res);
      const mappings = Array.isArray(page?.mappings) ? page.mappings : [];
      const primary = page?.primary_by_chain;
      const total = page?.member_total;
      return {
        mappings,
        next_cursor: page?.next_cursor ?? null,
        count: page?.count ?? mappings.length,
        primary_by_chain: primary && typeof primary === "object" ? primary : {},
        member_total: typeof total === "number" ? total : null,
        // "all" on a deployment that predates the lanes, which is the honest
        // reading: it served the paged index, and a caller must not caption an
        // unlabelled page as a curated one.
        lane: page?.lane === "core" ? "core" : "all",
        truncated: page?.truncated === true
      };
    },
    async putEntityMemberships(input) {
      const res = await client.post(memberships, {
        entity_id: input.entity_id,
        members: input.members.map((m) => ({
          chain: m.chain,
          address: m.address,
          // Only forward what the caller SET: an explicit `undefined` would be
          // dropped by JSON, but an explicit `null` would override the batch
          // default with nothing.
          ...m.role !== void 0 ? { role: m.role } : {},
          ...m.tier !== void 0 ? { tier: m.tier } : {},
          ...m.confidence !== void 0 ? { confidence: m.confidence } : {},
          ...m.valid_from !== void 0 ? { valid_from: m.valid_from } : {},
          ...m.valid_to !== void 0 ? { valid_to: m.valid_to } : {}
        })),
        ...input.role !== void 0 ? { role: input.role } : {},
        ...input.tier !== void 0 ? { tier: input.tier } : {},
        ...input.confidence !== void 0 ? { confidence: input.confidence } : {},
        ...input.source !== void 0 ? { source: input.source } : {},
        ...input.metadata !== void 0 ? { metadata: input.metadata } : {}
      });
      if (!res.ok && isMissingRoute(res)) throw new MissingRouteError(memberships, res);
      const out = unwrapData(res);
      const results = Array.isArray(out?.results) ? out.results : [];
      return {
        results,
        count: out?.count ?? results.filter((r) => r.ok).length,
        failed: out?.failed ?? results.filter((r) => !r.ok).length,
        mappings: Array.isArray(out?.mappings) ? out.mappings : []
      };
    },
    async enrich(chain, address, signal) {
      const res = unwrapData(
        await client.get(`${ref(chain, address)}/3rd-parties`, {
          signal,
          cacheTtlMs: 0
        })
      );
      return res.activity ?? null;
    },
    async listWalletTransactions(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/transactions`, {
          query: {
            ...query?.limit ? { limit: query.limit } : {},
            ...query?.cursor ? { cursor: query.cursor } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getDefi(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/defi`, {
          query: {
            ...query?.chain ? { chain: query.chain } : {},
            ...query?.protocol ? { protocol: query.protocol } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getNfts(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/nfts`, {
          query: {
            ...query?.limit ? { limit: query.limit } : {},
            ...query?.cursor ? { cursor: query.cursor } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getNftCollection(chain, address, query, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/nft-collection`, {
          query: {
            ...query?.limit ? { limit: query.limit } : {},
            ...query?.cursor ? { cursor: query.cursor } : {}
          },
          signal,
          cacheTtlMs: 0
        })
      );
    },
    async getDomain(chain, address, signal) {
      return unwrapData(
        await client.get(`${ref(chain, address)}/domain`, {
          signal,
          cacheTtlMs: 0
        })
      );
    }
  };
}

export { ADDRESS_DEFAULT_PAGE_LIMIT, ADDRESS_ENRICHMENT_STATUSES, ADDRESS_FIELD_DEFAULTS, ADDRESS_LIST_MODES, ADDRESS_MAX_PAGE_LIMIT, ADDRESS_ROW_SCOPES, ADDRESS_ROW_SCOPE_DESCRIPTIONS, ADDRESS_ROW_SCOPE_LABELS, ADDRESS_SCOPES, ADDRESS_TYPES, ADDRESS_TYPE_LABEL, LIFECYCLE_LABEL, MATRIX_ADDRESS_BUDGET, MATRIX_FULL_LIMIT, MAX_BULK_MEMBERS, MEMBERSHIP_ROLE_SUGGESTIONS, MEMBERSHIP_ROLE_VALUES, MissingRouteError, SEVERITY_LABEL, addressColorOf, addressEditableBody, addressLogoKeyOf, addressRefPath, addressRowScopeDescription, addressRowScopeLabel, addressTypeFromRoles, approvalLabel, boundMatrixMemberships, clampAddressLimit, createAddressesApi, defiKindFromLabel, describeWalletReadGap, existingPrimaryChains, groupAddressesByAddress, hasRecordedView, isBeingMapped, isContractLike, isContractType, isDefaultedField, isKnownNonContract, isVerified, lifecycleLabel, membershipMatrix, membershipRoleLabel, membershipRoleSuggestion, mergeWalletNftPages, nftCollectionViewToCollection, partitionWalletBalances, partitionWalletNfts, severityLabel, showsRecordedValue, sourceLabel, summarizeActivity, walletBalanceRows, walletBalancesGap, walletBalancesIsEmpty, walletBalancesOutcome, walletDataPaths, walletDefiIsEmpty, walletDefiToPositions, walletNftItemToDto, walletNftPageIsEmpty, walletNftsToDtos };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map